import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useSettings } from '../contexts/SettingsProvider';
import { analyzeWithOpenAI } from '../services/openai';
import { checkAndIncrementUsage } from '../services/analysisUsageService';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import SafeIcon from '../components/SafeIcon';
import ShieldDollarIcon from '../components/ShieldDollarIcon';
import AnalysisResult from '../components/AnalysisResult';
import AdvancedTradingChart from '../components/AdvancedTradingChart';
import AdvancedSettingsPanel from '../components/AdvancedSettingsPanel';
import UniversalPromptEditor from '../components/UniversalPromptEditor';
import TradingNewsWidget from '../components/TradingNewsWidget';
import { ProfessionalJournal, JournalStats } from '../components/journal';
import VoiceInterface from '../components/VoiceInterface';
import ProChatInterface from '../components/ProChatInterface';
import PlutusRealtime from '../components/PlutusRealtime';
import RiskProfileForm from '../components/RiskProfileForm';
import UpgradeDialog from '../components/UpgradeDialog';
import priceService from '../services/priceService';
import promptService from '../services/promptService';
import { saveJournalEntry } from '../services/journalService';
import { calculateTDIScore } from '@/services/disciplineScoreService';
import { supabase } from '@/integrations/supabase/client';
import snapChartLogo from '@/assets/snap-chart-logo.png';
import * as FiIcons from 'react-icons/fi';

const {
  FiTarget, FiActivity, FiMic, FiMicOff, FiSettings, FiBook, FiMessageCircle,
  FiBarChart2, FiPlay, FiPause, FiVolume2, FiVolumeX, FiRefreshCw,
  FiTrendingUp, FiDollarSign, FiClock, FiGlobe, FiEdit3, FiMaximize2, FiMinimize2, FiUser,
  FiCamera, FiImage, FiX, FiTerminal, FiCpu, FiShield, FiZap,
} = FiIcons;

const TradingInterface = () => {
  const { user } = useAuth();

  // Live discipline context — same data as the dashboard so the trader
  // sees their TDI score + streak while analyzing (don't break habit).
  const [tdiBadge, setTdiBadge] = useState(null);    // { tdi_score, tier }
  const [streakDays, setStreakDays] = useState(null);
  useEffect(() => {
    if (!user) return;
    calculateTDIScore(30).then((r) => {
      if (r?.success && r?.tdi_score != null) setTdiBadge(r);
    }).catch(() => {});
    supabase.rpc('get_my_dashboard_metrics', {
      p_tz_offset_minutes: -new Date().getTimezoneOffset(),
    }).then(({ data, error }) => {
      if (!error && data && typeof data.streak_days === 'number') setStreakDays(data.streak_days);
    });
  }, [user]);
  const { subscription, refreshProfile } = useSubscription();
  const { settings } = useSettings();
  const { flags, canAnalyze, analysisCheck, analysesRemaining, planInfo } = useFeatureFlags();
  
  // ✅ SUPER ADMIN CHECK - Only trendykingsuccess@gmail.com can access prompts
  const isSuperAdmin = user?.email === 'trendykingsuccess@gmail.com';

  // ✅ ÉTAT PRINCIPAL AVEC SYMBOLES CORRECTS
  const [selectedMarket, setSelectedMarket] = useState('Gold');
  const [selectedSymbol, setSelectedSymbol] = useState('XAUUSD');

  // ═══════════ FAVORITES (localStorage) — embryon de watchlist ═══════════
  // Pin assets you trade often so they show first in the quick-switch chips.
  const [favorites, setFavorites] = useState(() => {
    try { return JSON.parse(localStorage.getItem('tradingFavorites') || '[]'); }
    catch { return []; }
  });
  const isFavorite = favorites.includes(selectedSymbol);
  const toggleFavorite = () => {
    const next = isFavorite
      ? favorites.filter((f) => f !== selectedSymbol)
      : [...favorites, selectedSymbol];
    setFavorites(next);
    try { localStorage.setItem('tradingFavorites', JSON.stringify(next)); } catch { /* noop */ }
  };

  // Symbols per market (top picks first, rest after)
  const SYMBOLS_BY_MARKET = {
    Gold:    ['XAUUSD', 'XAUEUR'],
    Indices: ['US30', 'NAS100', 'SPP500', 'GER30'],
    Crypto:  ['BTCUSD', 'ETHUSD', 'SOLUSD', 'XRPUSD', 'ADAUSD', 'DOGEUSD'],
  };
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [tradeLevels, setTradeLevels] = useState(null);
  
  // ✅ CHART IMAGE FOR VISION ANALYSIS
  const [chartImage, setChartImage] = useState(null);
  const [chartImagePreview, setChartImagePreview] = useState(null);
  
  // ✅ UI STATES ORIGINAUX - MODALS COMME AVANT
  const [showPromptEditor, setShowPromptEditor] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [showVoiceInterface, setShowVoiceInterface] = useState(false);
  const [showProChat, setShowProChat] = useState(false);
  const [showRiskOfficer, setShowRiskOfficer] = useState(false);
  const [showTradingJournal, setShowTradingJournal] = useState(false);
  const [showJournalStats, setShowJournalStats] = useState(false);
  const [showRiskManagement, setShowRiskManagement] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('limit_reached');

  // ✅ PRIX TEMPS RÉEL DYNAMIQUES SELON LE MARCHÉ
  const [currentData, setCurrentData] = useState({
    price: 4190.35,
    change: 12.45,
    changePercent: 0.30,
    volume: '2.4M',
    high: 4195.80,
    low: 4175.20
  });

  // ✅ CALCUL DYNAMIQUE DE LA SESSION
  // Crypto runs 24/7 — never closed, not even on weekends. Forex/Indices/Gold
  // follow the standard Fri 22:00 UTC -> Sun 22:00 UTC weekend close.
  const getCurrentSession = (market) => {
    const now = new Date();
    const dayOfWeek = now.getUTCDay(); // 0 = Sunday, 6 = Saturday
    const utcHour = now.getUTCHours();

    // Crypto = 24/7, always open regardless of day/hour.
    if ((market || '').toLowerCase() === 'crypto') {
      return { name: '24/7', color: 'text-cyan-400', isOpen: true };
    }

    // Weekend check (gold / indices / forex only)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return { name: 'CLOSED', color: 'text-red-400', isOpen: false };
    }

    // Friday after 22:00 UTC = market closing
    if (dayOfWeek === 5 && utcHour >= 22) {
      return { name: 'CLOSING', color: 'text-orange-400', isOpen: false };
    }

    // Monday-Friday sessions
    if (utcHour >= 22 || utcHour < 8) {
      return { name: 'Asia', color: 'text-purple-400', isOpen: true };
    }
    if (utcHour >= 8 && utcHour < 13) {
      return { name: 'London', color: 'text-blue-400', isOpen: true };
    }
    if (utcHour >= 13 && utcHour < 17) {
      return { name: 'London/NY', color: 'text-cyan-400', isOpen: true };
    }
    if (utcHour >= 17 && utcHour < 22) {
      return { name: 'New York', color: 'text-green-400', isOpen: true };
    }

    return { name: 'Asia', color: 'text-purple-400', isOpen: true };
  };

  const currentSession = getCurrentSession(selectedMarket);

  // ✅ PROMPT PERSONNALISÉ CHARGÉ
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [promptLoading, setPromptLoading] = useState(true);

  // ✅ Track number of uploaded charts
  const [chartCount, setChartCount] = useState(0);

  // ✅ LISTEN TO CHART IMAGE UPLOAD FROM ANALYSIS RESULT COMPONENT
  useEffect(() => {
    const handleChartImageEvent = (event) => {
      const { imageData, allImages } = event.detail;
      const totalImages = allImages?.length || (imageData ? 1 : 0);
      
      console.log('📸 Received chart images from AnalysisResult:', {
        hasImage: !!imageData,
        totalImages: totalImages,
        allImagesCount: allImages?.length
      });
      
      // Store ALL images as array for multi-chart analysis
      if (allImages && allImages.length > 0) {
        const imagesData = allImages.map(img => img.data);
        setChartImage(imagesData); // Always store as array
        setChartImagePreview(imagesData[0]); // Preview first image
        setChartCount(imagesData.length);
      } else if (imageData) {
        setChartImage([imageData]); // Wrap single image in array
        setChartImagePreview(imageData);
        setChartCount(1);
      }
    };

    window.addEventListener('uploadChartImage', handleChartImageEvent);
    
    return () => {
      window.removeEventListener('uploadChartImage', handleChartImageEvent);
    };
  }, []);

  // ✅ REMOVE CHART IMAGE
  const removeChartImage = () => {
    setChartImage(null);
    setChartImagePreview(null);
    setChartCount(0);
  };

  // Auto-clear the queue after a successful analysis so the right panel
  // doesn't keep showing 'N CHARTS READY · Analyze N charts' on stale data.
  // The analysis result stays visible on the left; the queue resets so the
  // user can clearly drop a NEW chart for the next analysis.
  useEffect(() => {
    if (!analysisResult || chartCount === 0) return;
    const t = setTimeout(() => {
      setChartImage(null);
      setChartImagePreview(null);
      setChartCount(0);
    }, 1200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisResult]);

  // ✅ FONCTION POUR OBTENIR LA CLÉ DU PROMPT (individuel pour indices et crypto)
  const getPromptKey = () => {
    // Pour les indices, utiliser le symbole spécifique (us30, nas100, etc.)
    if (selectedMarket === 'Indices') {
      const symbolLower = selectedSymbol.toLowerCase();
      if (['us30', 'nas100', 'spp500', 'ger30'].includes(symbolLower)) {
        return symbolLower;
      }
      return 'indices';
    }
    // Pour les cryptos, utiliser le symbole spécifique (btcusd, ethusd, etc.)
    if (selectedMarket === 'Crypto') {
      const symbolLower = selectedSymbol.toLowerCase();
      const cryptoKeys = ['btcusd', 'ethusd', 'solusd', 'xrpusd', 'adausd', 'dogeusd', 'avaxusd', 'linkusd', 'maticusd', 'dotusd'];
      if (cryptoKeys.includes(symbolLower)) {
        return symbolLower;
      }
      return 'crypto';
    }
    return selectedMarket.toLowerCase();
  };

  // ✅ CHARGER LE PROMPT PERSONNALISÉ AU DÉMARRAGE ET AU CHANGEMENT DE SYMBOLE
  useEffect(() => {
    const loadUserPrompt = async () => {
      try {
        setPromptLoading(true);
        const promptKey = getPromptKey();
        console.log(`🔍 Loading user prompt for ${promptKey}...`);
        
        const userPrompt = await promptService.getPrompt(promptKey);
        
        if (userPrompt && userPrompt.trim().length > 0) {
          setCurrentPrompt(userPrompt);
          console.log(`✅ User prompt loaded for ${promptKey}:`, userPrompt.substring(0, 100) + '...');
        } else {
          console.log(`📋 No custom prompt found for ${promptKey}, using default`);
        }
        
      } catch (error) {
        console.error('❌ Error loading user prompt:', error);
      } finally {
        setPromptLoading(false);
      }
    };

    loadUserPrompt();
  }, [selectedMarket, selectedSymbol]); // ✅ Écouter aussi selectedSymbol

  // ✅ RESET ANALYSIS & CHARTS WHEN CHANGING MARKET/SYMBOL
  useEffect(() => {
    console.log(`🔄 Market/Symbol changed to ${selectedMarket}/${selectedSymbol} - clearing previous analysis`);
    setAnalysisResult(null);
    setAnalysisError(null);
    setChartImage(null);
    setChartImagePreview(null);
    setChartCount(0);
  }, [selectedMarket, selectedSymbol]);

  // ✅ PRIX TEMPS RÉEL AVEC PRICESERVICE SELON LE SYMBOLE SÉLECTIONNÉ
  useEffect(() => {
    console.log(`📊 Subscribing to real-time prices for ${selectedSymbol}...`);
    
    const subscriberId = priceService.subscribe((priceData) => {
      const symbolData = priceData[selectedSymbol];
      if (symbolData) {
        setCurrentData({
          price: symbolData.price,
          change: symbolData.change,
          changePercent: symbolData.changePercent,
          volume: symbolData.volume,
          high: symbolData.high,
          low: symbolData.low
        });
      }
    }, [selectedSymbol]);

    return () => {
      console.log(`📊 Unsubscribing from ${selectedSymbol} prices...`);
      priceService.unsubscribe(subscriberId);
    };
  }, [selectedSymbol]);

  // ✅ ANALYSE AVEC PROMPT PERSONNALISÉ ET PRIX RÉEL + IMAGE VISION
  const handleAnalysis = async (analysisData = null) => {
    try {
      setIsAnalyzing(true);
      setAnalysisError(null);
      setAnalysisResult(null);

      // ✅ CHECK SUBSCRIPTION & USAGE LIMITS
      if (!flags.canUseAiAnalysis) {
        setUpgradeReason('limit_reached');
        setShowUpgradeDialog(true);
        setIsAnalyzing(false);
        return;
      }

      // Check and increment usage (will fail if limit reached)
      const usageResult = await checkAndIncrementUsage();
      if (!usageResult.allowed) {
        // Show upgrade dialog instead of just error
        if (usageResult.error?.includes('limit') || usageResult.error?.includes('STARTER')) {
          setUpgradeReason('limit_reached');
          setShowUpgradeDialog(true);
        } else {
          setAnalysisError(usageResult.error || 'Analysis limit reached. Please upgrade your plan.');
        }
        setIsAnalyzing(false);
        // Refresh profile to update UI
        if (refreshProfile) refreshProfile();
        return;
      }

      const hasChartImage = chartCount > 0 && chartImage;
      console.log('🔥 Starting analysis:', {
        hasChartImage, 
        chartCount,
        hasCustomPrompt: currentPrompt.length > 50,
        session: currentSession.name,
        usageRemaining: usageResult.remaining
      });

      // ✅ UTILISER LE PROMPT PERSONNALISÉ DE L'UTILISATEUR
      let finalPrompt = '';
      
      // Use the session from component state
      const sessionText = currentSession.isOpen 
        ? currentSession.name 
        : `MARKET CLOSED (${currentSession.name})`;

      // Add vision context if image is provided
      const chartCountText = Array.isArray(chartImage) ? chartImage.length : 1;
      const visionContext = hasChartImage ? `
📸 CHART IMAGE ANALYSIS (${chartCountText} chart${chartCountText > 1 ? 's' : ''} provided):
You are provided with chart screenshot(s). Please analyze:
- Current price action and candlestick patterns
- Key support and resistance levels visible
- Trend direction and momentum
- Any technical indicators shown
- Volume patterns if visible
- **READ THE EXACT CURRENT PRICE** from the chart display (top-left or price axis)

📊 CRITICAL: Use the EXACT price shown on the TradingView chart - it is more accurate than any reference price provided.

` : '';
      
      if (currentPrompt && currentPrompt.trim().length > 50) {
        // Utiliser le prompt personnalisé + contexte prix + vision
        const priceInstruction = hasChartImage 
          ? `📊 READ THE EXACT CURRENT PRICE from the chart image - this is the most accurate price!
Our reference price is ~$${currentData.price.toFixed(2)} but USE THE CHART PRICE for your analysis.`
          : `💰 Current ${selectedSymbol} Price: $${currentData.price.toFixed(2)}`;
        
        finalPrompt = `${currentPrompt}

${visionContext}🔥 MARKET CONTEXT:
${priceInstruction}
📈 Session: ${sessionText}

⚠️ CRITICAL INSTRUCTIONS:
${hasChartImage ? `- READ THE EXACT PRICE from the TradingView chart image (it shows the real live price)
- ANALYZE THE ${chartCountText} CHART IMAGE(S) for patterns, support/resistance, trend structure
- Base your Entry, SL, TP levels on the CHART PRICE, not the reference price` : `- Use the price of $${currentData.price.toFixed(2)} as reference`}
- Provide specific Entry, Stop Loss, TP1, TP2, TP3 levels
- Include Direction (LONG/SHORT), Risk:Reward ratio, and Confidence %
- All analysis MUST be in ENGLISH ONLY
- Be institutional and actionable

BEGIN ${selectedSymbol} ANALYSIS NOW${hasChartImage ? ` (WITH ${chartCountText} CHART VISION)` : ''}:`;
      } else {
        // Fallback vers prompt simple si pas de prompt personnalisé
        const priceContext = hasChartImage 
          ? `📊 READ THE EXACT CURRENT PRICE from the chart image - this is the most accurate!
Our reference: ~$${currentData.price.toFixed(2)} (may be delayed)`
          : `💰 ${selectedSymbol} Price: $${currentData.price.toFixed(2)}`;
        
        finalPrompt = `You are Snap Chart AI, a professional ${selectedMarket} trading analyst with 15+ years of Wall Street experience.

${visionContext}**Current Market Context:**
${priceContext}
- Session: ${sessionText}

**Required Analysis Format:**
1. Market Direction (LONG/SHORT)
2. Entry Price: $XXXX.XX
3. Stop Loss (SL): $XXXX.XX  
4. Take Profit 1 (TP1): $XXXX.XX
5. Take Profit 2 (TP2): $XXXX.XX
6. Take Profit 3 (TP3): $XXXX.XX
7. Risk:Reward ratio
8. Confidence level (%)

**CRITICAL Requirements:**
- Analysis MUST be in ENGLISH ONLY
${hasChartImage ? `- READ THE EXACT PRICE from the TradingView chart image (it shows the real live price)
- ANALYZE THE ${chartCountText} CHART IMAGE(S) for patterns, support/resistance levels
- Base your Entry, SL, TP levels on the CHART PRICE` : `- Use the price of $${currentData.price.toFixed(2)} as reference`}
- Include specific price levels with dollar amounts
- Provide institutional reasoning with clear entry/exit levels

BEGIN ${selectedSymbol} ANALYSIS NOW${hasChartImage ? ` (WITH ${chartCountText} CHART VISION)` : ''}:`;
      }

      console.log('📝 Final prompt prepared:', {
        hasCustomPrompt: currentPrompt.length > 50,
        hasChartImage,
        promptLength: finalPrompt.length,
        currentPrice: currentData.price,
        symbol: selectedSymbol,
        market: selectedMarket
      });

      // ✅ Capture chartImage NOW (before any state changes / effects that
      // could clear it). We reuse this snapshot for the auto-upload later
      // so the screenshot always makes it to storage even if a 1200ms
      // useEffect timer clears the visible state mid-await.
      const chartImageSnapshot = chartImage;

      // ✅ ANALYSE AVEC OPENAI (+ IMAGE SI FOURNIE)
      const response = await analyzeWithOpenAI(finalPrompt, null, {
        imageBase64: chartImageSnapshot || null
      });
      
      // Extract the analysis text from OpenAI response
      const analysisText = response?.choices?.[0]?.message?.content || response?.analysis_result;
      
      if (!analysisText) {
        throw new Error('No analysis content received from OpenAI');
      }
      
      const result = {
        analysis_result: analysisText,
        model: response?.model || 'unknown',
        usage: response?.usage
      };
      
      console.log('✅ Analysis completed:', result);
      
      if (result && result.analysis_result) {
        setAnalysisResult({
          ...result,
          timestamp: new Date().toISOString(),
          market: selectedMarket,
          symbol: selectedSymbol,
          current_price: currentData.price,
        used_custom_prompt: currentPrompt.length > 50
        });
        
        // ✅ AUTO-SAVE TO PROFESSIONAL JOURNAL (non-blocking)
        // Bug fix: previously we skipped saves when text < 150 chars AND had no
        // 'WAIT'/'TRADE'/'LONG'/'SHORT'/etc. keywords. This dropped CRYPTO and
        // INDICES analyses whose brief format ('WATCH - MONITORING', 'AWAITING
        // BREAKOUT', 'HOLD POSITION', etc.) contained none of the whitelisted
        // words. Now we save aggressively: any text >= 50 chars from OpenAI is
        // saved. Only true error blobs (empty / < 50 chars) are skipped.
        const analysisText = result.analysis_result;

        console.log('📝 Journal save check:', {
          hasAnalysisText: !!analysisText,
          textLength: analysisText?.length || 0,
          preview: analysisText?.substring(0, 120) + '...'
        });

        // Expanded keyword whitelist — now includes WATCH / MONITORING / HOLD
        // / AWAITING / BREAKOUT / RESISTANCE / SUPPORT so crypto & indices
        // brief formats aren't dropped.
        const hasTradeDecision = analysisText && (
          analysisText.toUpperCase().includes('WAIT') ||
          analysisText.toUpperCase().includes('WATCH') ||
          analysisText.toUpperCase().includes('MONITORING') ||
          analysisText.toUpperCase().includes('AWAITING') ||
          analysisText.toUpperCase().includes('HOLD') ||
          analysisText.toUpperCase().includes('TRADE') ||
          analysisText.toUpperCase().includes('NO_TRADE') ||
          analysisText.toUpperCase().includes('NO-TRADE') ||
          analysisText.toUpperCase().includes('LONG') ||
          analysisText.toUpperCase().includes('SHORT') ||
          analysisText.toUpperCase().includes('ENTRY') ||
          analysisText.toUpperCase().includes('STOP LOSS') ||
          analysisText.toUpperCase().includes('BREAKOUT') ||
          analysisText.toUpperCase().includes('RESISTANCE') ||
          analysisText.toUpperCase().includes('SUPPORT')
        );

        // Only skip if truly unusable — empty or extremely short (< 50 chars).
        // We used to require 150 chars, which dropped legitimate brief crypto
        // verdicts. Lowered threshold so every real analysis reaches the journal.
        const isErrorMessage = !analysisText || analysisText.length < 50;
        
        console.log('📝 isErrorMessage check:', isErrorMessage, {
          noText: !analysisText,
          tooShort: (analysisText?.length ?? 0) < 50,
          hasTradeDecision: hasTradeDecision
        });
        
        if (!isErrorMessage) {
          console.log('📓 Starting journal save with data:', {
            marketType: selectedMarket.toLowerCase(),
            assetSymbol: selectedSymbol,
            session: currentSession.isOpen ? currentSession.name : 'CLOSED',
            inputPrice: currentData.price,
            chartCount: chartCount || 0,
          });

          // Upload chart image to trade-screenshots bucket so the journal
          // detail modal can render it later. Non-blocking: if upload fails,
          // save still proceeds without the screenshot (better than losing
          // the whole entry).
          let uploadedScreenshotPath = null;
          let uploadedScreenshotPaths = [];
          try {
            const { data: { user: authUser } } = await supabase.auth.getUser();
            // Snapshot captured at handleAnalyze entry (chartImage state may
            // already have been cleared by the 1200ms auto-reset effect).
            // We now upload ALL charts, not just the first one, so multi-chart
            // analyses show every chart in the journal gallery.
            const allImages = Array.isArray(chartImageSnapshot) ? chartImageSnapshot
                            : (typeof chartImageSnapshot === 'string' ? [chartImageSnapshot] : []);
            console.log('🖼 Multi-chart upload check:', {
              hasAuthUser: !!authUser,
              chartCount: allImages.length,
              firstImagePrefix: allImages[0]?.substring?.(0, 30) ?? 'n/a',
            });
            if (authUser && allImages.length > 0) {
              const baseTs = Date.now();
              for (let i = 0; i < allImages.length; i++) {
                const img = allImages[i];
                if (!img || typeof img !== 'string' || !img.startsWith('data:')) continue;
                try {
                  const blob = await (await fetch(img)).blob();
                  const mimeMatch = img.match(/^data:image\/([a-z]+);/i);
                  const ext = mimeMatch ? mimeMatch[1].toLowerCase() : 'png';
                  // Suffix each path with the index so multi-chart uploads in the
                  // same millisecond don't collide.
                  const path = `${authUser.id}/${baseTs}_${i}.${ext}`;
                  const { error: upErr } = await supabase.storage
                    .from('trade-screenshots')
                    .upload(path, blob, { contentType: `image/${ext}`, upsert: false });
                  if (upErr) {
                    console.warn(`⚠️ Chart ${i + 1}/${allImages.length} upload failed:`, upErr);
                  } else {
                    uploadedScreenshotPaths.push(path);
                    console.log(`✅ Chart ${i + 1}/${allImages.length} uploaded:`, path);
                  }
                } catch (imgEx) {
                  console.warn(`⚠️ Chart ${i + 1} upload threw:`, imgEx);
                }
              }
              // Mirror the first path into the legacy single-value field.
              uploadedScreenshotPath = uploadedScreenshotPaths[0] || null;
            }
          } catch (upEx) {
            console.warn('⚠️ Screenshot upload threw (entry will save without it):', upEx);
          }

          // Save with retry logic - AWAIT to ensure it completes
          const saveWithRetry = async (attempt = 1) => {
            try {
              console.log(`📓 Journal save attempt ${attempt}...`);
              const entry = await saveJournalEntry({
                marketType: selectedMarket.toLowerCase(),
                assetSymbol: selectedSymbol,
                session: currentSession.isOpen ? currentSession.name : 'CLOSED',
                inputPrice: currentData.price,
                analysisText: analysisText,
                modelUsed: result.model || 'gpt-5.5',
                chartCount: chartCount || 0,
                screenshotUrl: uploadedScreenshotPath,
                screenshotUrls: uploadedScreenshotPaths.length > 0 ? uploadedScreenshotPaths : null,
              });
              
              if (entry) {
                console.log(`✅ Journal entry saved successfully: ${entry.id} (Decision: ${entry.executionPermission})`);
                // ✅ AUTO-RESET chart images after successful save - user can now upload new charts
                setChartImage(null);
                setChartImagePreview(null);
                setChartCount(0);
                console.log('🔄 Chart images cleared - ready for new analysis');
              } else if (attempt < 3) {
                console.warn(`⚠️ Journal save returned null (attempt ${attempt}), retrying...`);
                await new Promise(r => setTimeout(r, 500)); // Small delay before retry
                await saveWithRetry(attempt + 1);
              } else {
                console.error('❌ Journal entry was not saved after 3 retries');
              }
            } catch (err) {
              console.error(`❌ Journal save error (attempt ${attempt}):`, err);
              if (attempt < 3) {
                console.warn(`⚠️ Retrying journal save...`);
                await new Promise(r => setTimeout(r, 500));
                await saveWithRetry(attempt + 1);
              } else {
                console.error('❌ Journal save failed after 3 retries:', err.message);
              }
            }
          };
          
          // Execute save (fire and forget but with logging)
          saveWithRetry().catch(err => {
            console.error('❌ Unexpected journal save error:', err);
          });
        } else {
          console.log('⚠️ Skipped journal save: AI response was incomplete or an error message', {
            length: analysisText?.length,
            sample: analysisText?.substring(0, 50)
          });
        }
      } else {
        throw new Error('No analysis result received');
      }

    } catch (error) {
      console.error('❌ Analysis error:', error);
      setAnalysisError(error.message || 'Analysis failed');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // ✅ HANDLERS POUR LES SYMBOLES AVEC PRIX CORRECTS
  const handleSymbolChange = (symbol) => {
    console.log(`🔄 Switching to symbol: ${symbol}`);
    // Auto-detect market from symbol so clicking a favorite from another
    // market (e.g. BTCUSD chip while market is 'Gold') updates BOTH so
    // the analysis panel + session pill reflect the correct context.
    const up = (symbol || '').toUpperCase();
    const CRYPTO_BASES = ['BTC','ETH','SOL','XRP','ADA','DOGE','AVAX','LINK','MATIC','DOT'];
    const INDICES = ['US30','NAS100','SPP500','GER30'];
    let detectedMarket = selectedMarket;
    if (up.startsWith('XAU')) detectedMarket = 'Gold';
    else if (INDICES.includes(up)) detectedMarket = 'Indices';
    else if (CRYPTO_BASES.some((b) => up.includes(b))) detectedMarket = 'Crypto';
    setSelectedSymbol(symbol);
    if (detectedMarket !== selectedMarket) {
      console.log(`↪️ Auto-switching market to ${detectedMarket} (was ${selectedMarket})`);
      setSelectedMarket(detectedMarket);
    }
  };

  const handleMarketChange = (market) => {
    console.log(`🔄 Switching to market: ${market}`);
    setSelectedMarket(market);
    
    // ✅ RESET SYMBOL SELON LE MARCHÉ AVEC SYMBOLES CORRECTS
    if (market === 'Gold') setSelectedSymbol('XAUUSD');
    else if (market === 'Indices') setSelectedSymbol('US30'); // Défaut sur US30 au lieu de NAS100
    else if (market === 'Crypto') setSelectedSymbol('BTCUSD');
  };

  // ✅ AFFICHAGE DU PRIX DANS LE HEADER SELON LE MARCHÉ
  const getDisplayName = () => {
    if (selectedMarket === 'Gold') return 'Gold';
    if (selectedMarket === 'Indices') {
      switch(selectedSymbol) {
        case 'US30': return 'US30';
        case 'NAS100': return 'NAS100';
        case 'SPP500': return 'SPP500';
        case 'GER30': return 'GER30';
        default: return 'Indices';
      }
    }
    if (selectedMarket === 'Crypto') {
      // Return the crypto symbol without USD
      return selectedSymbol.replace('USD', '');
    }
    return selectedMarket;
  };

  const getPriceUnit = () => {
    if (selectedMarket === 'Gold') return '$';
    if (selectedMarket === 'Indices') return '';
    if (selectedMarket === 'Crypto') return '$';
    return '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black">
      <div className="container mx-auto px-4 py-6 space-y-6">
        
        {/* ✅ HEADER ÉPURÉ 2026 - PURPLE/CYAN THEME */}
        <div className="relative overflow-hidden rounded-2xl border border-purple-500/30 bg-gray-900/80 backdrop-blur-xl shadow-lg shadow-purple-500/10">
          
          <div className="relative px-6 py-5">
            <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-5">

              {/* ═══════════ BRAND + TIER + LIVE TDI + STREAK + ASSET PRICE ═══════════ */}
              <div className="flex items-center gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="inline-block px-2.5 py-0.5 rounded-md bg-cyan-500/10 border border-cyan-500/30 text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-300">
                      institutional plan
                    </span>
                    {/* Live TDI badge — same data as the dashboard hero */}
                    {tdiBadge?.tdi_score != null && (() => {
                      const score = tdiBadge.tdi_score;
                      const tier = (tdiBadge.tier || (
                        score >= 740 ? 'EXCELLENT' :
                        score >= 670 ? 'GOOD' :
                        score >= 580 ? 'FAIR' : 'DEVELOPING'
                      )).toUpperCase();
                      const tierColor = tier === 'EXCELLENT' ? 'emerald' : tier === 'GOOD' ? 'cyan' : tier === 'FAIR' ? 'purple' : 'amber';
                      return (
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[10px] font-mono uppercase tracking-[0.15em] ${
                          tierColor === 'emerald' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' :
                          tierColor === 'cyan'    ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300' :
                          tierColor === 'purple'  ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' :
                                                    'bg-amber-500/10 border-amber-500/30 text-amber-300'
                        }`}>
                          <SafeIcon icon={FiShield} className="text-[9px]" /> tdi {score} · {tier.toLowerCase()}
                        </span>
                      );
                    })()}
                    {/* Streak badge — orange flame when active */}
                    {streakDays != null && streakDays > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border border-orange-500/30 bg-orange-500/10 text-[10px] font-mono uppercase tracking-[0.15em] text-orange-300">
                        🔥 {streakDays}d streak
                      </span>
                    )}
                  </div>
                  <h1 className="text-2xl font-black text-white tracking-tight leading-none">
                    <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                      {selectedMarket} Analysis
                    </span>
                  </h1>
                </div>

                {/* Asset price — big mono institutional terminal style */}
                <div className="flex items-baseline gap-3 pl-4 ml-2 border-l border-white/10">
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500">
                    {getDisplayName()}
                  </span>
                  <span className="text-2xl font-black font-mono text-white tabular-nums tracking-tight">
                    {getPriceUnit()}{currentData.price.toLocaleString()}
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-mono font-bold ${
                    currentData.change >= 0
                      ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30'
                      : 'bg-red-500/15 text-red-300 border border-red-500/30'
                  }`}>
                    {currentData.change >= 0 ? '↑' : '↓'} {Math.abs(currentData.changePercent).toFixed(2)}%
                  </span>
                </div>
              </div>

              {/* ═══════════ TOOL CHIPS — unified v3 mono uppercase ═══════════ */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { label: 'journal',    onClick: () => setShowTradingJournal(true), icon: FiBook },
                  { label: 'discipline', onClick: () => setShowJournalStats(true),    icon: FiBarChart2 },
                  { label: 'risk',       onClick: () => setShowRiskManagement(true),  icon: FiShield },
                  { label: 'plutus ai',  onClick: () => setShowRiskOfficer(true),     icon: FiUser },
                ].map((c) => (
                  <button key={c.label} onClick={c.onClick}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.02] text-gray-300 hover:border-cyan-400/40 hover:bg-cyan-500/5 hover:text-cyan-300 text-xs font-mono uppercase tracking-[0.15em] transition">
                    <SafeIcon icon={c.icon} className="text-xs" /> {c.label}
                  </button>
                ))}
                {/* Prompts + AI config moved to /admin → AI Model Config — NOT visible here even for admins */}
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════ ASSET SELECTOR — quick-switch chips + favorites ═══════════ */}
        <div className="rounded-2xl border border-white/10 bg-gray-900/40 p-4 mb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            {/* Current asset + favorite star */}
            <div className="flex items-center gap-3">
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ asset</span>
              <span className="text-lg font-black font-mono text-white tabular-nums">{selectedSymbol}</span>
              <button onClick={toggleFavorite}
                title={isFavorite ? 'Remove from favorites' : 'Pin to favorites'}
                className={`p-1.5 rounded-md transition ${
                  isFavorite
                    ? 'bg-amber-500/15 border border-amber-500/40 text-amber-300'
                    : 'border border-white/10 text-gray-500 hover:text-amber-300 hover:border-amber-500/40'
                }`}>
                <span className="text-sm leading-none">{isFavorite ? '★' : '☆'}</span>
              </button>
            </div>
            {/* Market tabs */}
            <div className="flex items-center gap-1.5">
              {['Gold', 'Indices', 'Crypto'].map((m) => (
                <button key={m} onClick={() => handleMarketChange(m)}
                  className={`px-3 py-1.5 rounded-lg text-[11px] font-mono uppercase tracking-[0.15em] transition ${
                    selectedMarket === m
                      ? 'bg-cyan-500/15 border border-cyan-400/40 text-cyan-300'
                      : 'border border-white/10 text-gray-400 hover:text-cyan-300 hover:border-cyan-400/30'
                  }`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Quick-switch chips — favorites first, then market default symbols */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {/* Show favorited symbols across all markets first (with ★) */}
            {favorites.length > 0 && favorites.filter((s) => s !== selectedSymbol).slice(0, 3).map((s) => (
              <button key={`fav-${s}`} onClick={() => handleSymbolChange(s)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-amber-500/30 bg-amber-500/5 text-amber-300 text-[11px] font-mono uppercase tracking-wider hover:bg-amber-500/10 transition">
                <span className="text-xs leading-none">★</span>{s}
              </button>
            ))}
            {/* Then current-market symbols (excluding already shown) */}
            {(SYMBOLS_BY_MARKET[selectedMarket] || []).filter((s) => s !== selectedSymbol && !favorites.includes(s)).map((s) => (
              <button key={s} onClick={() => handleSymbolChange(s)}
                className="px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.02] text-gray-400 text-[11px] font-mono uppercase tracking-wider hover:text-cyan-300 hover:border-cyan-400/30 hover:bg-cyan-500/5 transition">
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* ═══════════ TERMINAL CHART — v3 institutional ═══════════ */}
        <div className="bg-gray-900/60 rounded-2xl border border-white/10 overflow-hidden shadow-2xl shadow-cyan-500/5">
          <div className="px-5 py-3 border-b border-white/5 bg-gray-900/40">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-emerald-300">live · ai terminal</span>
              <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-gray-600 ml-2">verdict in 2s</span>
              {/* Market selector removed — single source of truth is the
                  asset selector section above the chart card. */}
            </div>
          </div>
          <div className="h-[600px]">
            <AdvancedTradingChart
              symbol={selectedSymbol}
              market={selectedMarket}
              onSymbolChange={handleSymbolChange}
              onChartAnalysis={handleAnalysis}
              currentData={currentData}
              isAnalyzing={isAnalyzing}
              capturedCount={Array.isArray(chartImage) ? chartImage.length : (chartImage ? 1 : 0)}
              onChartCaptured={(img) => {
                setChartImage((prev) => {
                  const arr = Array.isArray(prev) ? prev : (prev ? [prev] : []);
                  return [...arr, img.preview];
                });
                setChartImagePreview(img.preview);
                setChartCount((prev) => (typeof prev === 'number' ? prev + 1 : 1));
                window.dispatchEvent(new CustomEvent('chart-images-uploaded', {
                  detail: { imageData: img.preview, allImages: [{ data: img.preview }] }
                }));
              }}
            />
          </div>
        </div>

        {/* ✅ INTERFACE D'ANALYSE - ÉPURÉ 2026 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* ✅ RÉSULTATS D'ANALYSE - GAUCHE */}
          <div className="lg:order-1 h-full">
            <AnalysisResult
              key={`${selectedMarket}-${selectedSymbol}`}
              analysisResult={analysisResult}
              analyzedChartImage={Array.isArray(chartImage) ? chartImage[0] : (chartImagePreview || chartImage)}
              isLoading={isAnalyzing}
              error={analysisError}
              onExtractTradeLevels={setTradeLevels}
              onOpenPromptEditor={null}
              onClearAnalysis={() => {
                setAnalysisResult(null);
                setAnalysisError(null);
                setTradeLevels(null);
                setChartImage(null);
                setChartImagePreview(null);
                setChartCount(0);
              }}
              onRetryAnalysis={() => {
                setAnalysisResult(null);
                setAnalysisError(null);
                handleAnalysis();
              }}
            />
          </div>

          {/* ═══════════ ANALYSIS PANEL — v3 institutional ═══════════ */}
          <div className="lg:order-2 bg-gray-900/60 rounded-2xl border border-white/10 flex flex-col">
            <div className="px-5 py-3 border-b border-white/5 bg-gray-900/40">
              <div className="flex justify-between items-center">
                <div>
                  <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">▸ ai analysis</span>
                  <h3 className="text-base font-bold text-white mt-0.5">
                    {selectedMarket} Analysis
                  </h3>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-mono uppercase tracking-[0.15em] ${
                  currentSession.isOpen
                    ? 'bg-emerald-500/15 border border-emerald-500/30 text-emerald-300'
                    : 'bg-gray-500/15 border border-white/10 text-gray-400'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${currentSession.isOpen ? 'bg-emerald-400 animate-pulse' : 'bg-gray-500'}`} />
                  {currentSession.name}
                </span>
              </div>
            </div>

            <div className="flex-1 p-4 flex flex-col">
              <div className="flex-1 space-y-4">

                {/* Context metadata grid — v3 mono labels */}
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'price',   value: `${getPriceUnit()}${currentData.price.toLocaleString()}`, mono: true },
                    { label: 'session', value: currentSession.name, mono: true },
                    { label: 'symbol',  value: selectedSymbol, mono: true },
                    { label: 'output',  value: 'English' },
                  ].map((f) => (
                    <div key={f.label} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                      <div className="text-[9px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-1">{f.label}</div>
                      <div className={`text-sm font-bold text-white ${f.mono ? 'font-mono tabular-nums' : ''}`}>{f.value}</div>
                    </div>
                  ))}
                </div>

                {/* ✅ PROMPT STATUS - Hidden from UI but functionality remains */}
                <div className="hidden">
                  <h4 className="text-purple-400 font-medium mb-2 flex items-center gap-2 text-sm">
                    <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                    {promptLoading ? 'Loading Prompt...' : 
                     currentPrompt.length > 50 ? 'Custom Prompt Active' : 
                     'Default Institutional System'}
                  </h4>
                  
                  {promptLoading ? (
                    <div className="text-sm text-gray-400 flex items-center gap-2">
                      <SafeIcon icon={FiRefreshCw} className="animate-spin w-4 h-4" />
                      Loading your personalized prompt...
                    </div>
                  ) : currentPrompt.length > 50 ? (
                    <div className="text-sm text-gray-400 space-y-2">
                      <p className="text-cyan-400 font-medium text-xs">Your custom prompt is loaded and active</p>
                      <p className="text-xs text-gray-500 line-clamp-2">
                        {currentPrompt.substring(0, 150)}...
                      </p>
                      <div className="text-xs text-purple-300/70 bg-purple-500/10 rounded-lg px-3 py-2">
                        <span className="font-medium">{currentPrompt.length}</span> characters • Ready
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 space-y-1">
                      <p>• English analysis with price levels</p>
                      <p>• R:R ratio & probability %</p>
                    </div>
                  )}
                </div>

                {/* Chart queue state — empty: minimal v3 prompt; queued: clean pill */}
                {chartCount === 0 && !isAnalyzing && !analysisResult ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-14 h-14 rounded-xl border-2 border-dashed border-white/10 bg-white/[0.02] flex items-center justify-center mb-3">
                      <SafeIcon icon={FiCamera} className="w-6 h-6 text-gray-600" />
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-gray-500 mb-1">awaiting chart</span>
                    <p className="text-sm text-gray-400 max-w-xs">
                      Drop or capture a chart — the AI gives you a verdict in <span className="text-cyan-400 font-semibold">2 seconds</span>.
                    </p>
                  </div>
                ) : chartCount > 0 ? (
                  <div className="rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                        <span className="text-xs font-mono uppercase tracking-[0.15em] text-cyan-300">
                          {chartCount} chart{chartCount > 1 ? 's' : ''} ready
                        </span>
                      </div>
                      <button
                        onClick={removeChartImage}
                        className="text-[10px] font-mono uppercase tracking-wider text-gray-500 hover:text-red-300 transition"
                      >
                        clear
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* ✅ BOUTON D'ANALYSE */}
              <div className="pt-4">
                {/* Usage Badge */}
                {flags.aiAnalysesLimit !== null && flags.canUseAiAnalysis && (
                  <div className="flex justify-center mb-3">
                    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${
                      (analysesRemaining ?? 0) <= 3 
                        ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' 
                        : 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
                    }`}>
                      <span>{analysesRemaining ?? 0}/{flags.aiAnalysesLimit} analyses left</span>
                      {(analysesRemaining ?? 0) <= 3 && <span className="text-amber-300">• Upgrade to Pro Trader</span>}
                    </div>
                  </div>
                )}
                
                <button
                  onClick={handleAnalysis}
                  disabled={isAnalyzing || promptLoading || !canAnalyze || chartCount === 0}
                  className={`w-full py-4 px-6 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-3 ${
                    isAnalyzing || promptLoading || !canAnalyze || chartCount === 0
                      ? 'bg-white/[0.03] border border-white/10 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-cyan-400 to-purple-400 text-black hover:scale-[1.01] shadow-xl shadow-cyan-500/20'
                  }`}
                >
                  <SafeIcon icon={isAnalyzing ? FiRefreshCw : chartCount > 0 ? FiZap : FiCpu} className={`w-4 h-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
                  <span>
                    {!canAnalyze ? (planInfo.name === 'No Plan' ? 'Subscription Required' : 'Limit Reached — Upgrade') :
                     chartCount === 0 ? 'Drop a chart to analyze' :
                     isAnalyzing ? `Analyzing ${selectedSymbol}…` :
                     promptLoading ? 'Loading…' :
                     `⚡ Analyze ${chartCount} chart${chartCount > 1 ? 's' : ''} →`}
                  </span>
                </button>
                
                {/* Upload instruction when no chart */}
                {chartCount === 0 && (
                  <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
                    <span className="text-amber-500/80 font-medium">REQUIRED:</span> Upload charts to enable analysis.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ MODALS ORIGINAUX - TOUS FONCTIONNELS */}
      <UniversalPromptEditor
        isOpen={showPromptEditor}
        onClose={() => {
          setShowPromptEditor(false);
          // Recharger le prompt après fermeture de l'éditeur
          setTimeout(async () => {
            try {
              const promptKey = getPromptKey();
              const userPrompt = await promptService.getPrompt(promptKey);
              if (userPrompt) {
                setCurrentPrompt(userPrompt);
                console.log(`✅ Prompt reloaded for ${promptKey} after editor close`);
              }
            } catch (error) {
              console.error('❌ Error reloading prompt:', error);
            }
          }, 500);
        }}
        market={getPromptKey()}
      />

      <AdvancedSettingsPanel
        isOpen={showAdvancedSettings}
        onClose={() => setShowAdvancedSettings(false)}
      />

      {showVoiceInterface && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col border border-white/10">
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <div className="w-6 h-6 rounded-md bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
                  <SafeIcon icon={FiCpu} className="w-3.5 h-3.5 text-cyan-400" />
                </div>
                Plutus AI Voice
              </h2>
              <button 
                onClick={() => setShowVoiceInterface(false)}
                className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <VoiceInterface />
            </div>
          </div>
        </div>
      )}

      {showProChat && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-6xl max-h-[90vh]">
            <ProChatInterface 
              currentPrice={currentData.price}
              selectedMarket={selectedMarket}
              onClose={() => setShowProChat(false)}
            />
          </div>
        </div>
      )}

      {/* Unified Plutus AI Risk Officer */}
      {showRiskOfficer && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-4xl h-[85vh]">
            <PlutusRealtime
              currentPrice={currentData.price}
              selectedMarket={selectedMarket}
              chartImage={Array.isArray(chartImage) ? chartImage[0] : (chartImagePreview || chartImage)}
              onClose={() => setShowRiskOfficer(false)}
            />
          </div>
        </div>
      )}

      {/* ✅ PROFESSIONAL TRADING JOURNAL */}
      <ProfessionalJournal
        isOpen={showTradingJournal}
        onClose={() => setShowTradingJournal(false)}
      />

      {/* ✅ BEHAVIOR & DISCIPLINE MODAL */}
      {showJournalStats && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={() => setShowJournalStats(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-6xl max-h-[90vh] bg-gradient-to-b from-gray-900 to-gray-950 rounded-2xl border border-purple-500/30 overflow-hidden shadow-2xl shadow-purple-500/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-purple-500/20 bg-gray-900/80">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 flex items-center justify-center">
                  <SafeIcon icon={FiBarChart2} className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Behavior & Discipline</h2>
                  <p className="text-xs text-gray-500">TDI Score & Capital Preservation</p>
                </div>
              </div>
              <button
                onClick={() => setShowJournalStats(false)}
                className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
              <JournalStats />
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* ✅ RISK MANAGEMENT MODAL */}
      {showRiskManagement && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-purple-500/20 w-full max-w-2xl max-h-[85vh] overflow-hidden"
          >
            <div className="p-6 border-b border-gray-800/50 bg-gradient-to-r from-purple-500/10 to-cyan-500/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-xl blur-lg opacity-50"></div>
                    <div className="relative p-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                      <SafeIcon icon={FiShield} className="w-5 h-5 text-cyan-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Risk Management</h3>
                    <p className="text-sm text-gray-400">Configure your trading risk parameters</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowRiskManagement(false)}
                  className="p-2 rounded-xl bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 hover:from-purple-600/20 hover:to-cyan-600/20 text-gray-400 hover:text-cyan-400 transition-all"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-100px)]">
              <RiskProfileForm onSave={() => {
                setTimeout(() => {
                  setShowRiskManagement(false);
                }, 1500);
              }} />
            </div>
          </motion.div>
        </div>
      )}

      {/* ✅ UPGRADE DIALOG */}
      <UpgradeDialog
        isOpen={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        reason={upgradeReason}
        currentPlan={planInfo?.tier || 'none'}
      />
    </div>
  );
};

export default TradingInterface;